/**
 * JWT decode helpers — decode-only. Signature verification is the backend's job.
 *
 * The access token exp claim is read to drive proactive refresh (Plan 04 timer);
 * the full claims object is surfaced to callers that need `tenant_id` / `roles` /
 * `aud`. Both helpers return `null` rather than throwing on malformed input so
 * callers can treat decode errors as "not authenticated" without try/catch noise.
 */
import { jwtDecode } from "jwt-decode";
import type { AuthClaims } from "../types.js";

/**
 * Decode the `exp` claim (seconds since epoch). Returns `null` on malformed input
 * or when the `exp` claim is missing / non-numeric.
 */
export function decodeJwtExp(token: string): number | null {
  if (token === "") return null;
  try {
    const claims = jwtDecode<{ exp?: unknown }>(token);
    return typeof claims.exp === "number" ? claims.exp : null;
  } catch {
    return null;
  }
}

/**
 * Decode full `AuthClaims`. Returns `null` on malformed input. No runtime
 * shape validation — trust is the backend's responsibility; a malformed
 * shape here means "pretend unauthenticated" rather than "throw".
 */
export function decodeJwtClaims(token: string): AuthClaims | null {
  if (token === "") return null;
  try {
    return jwtDecode<AuthClaims>(token);
  } catch {
    return null;
  }
}
